import * as rrd from 'react-router-dom';
import { useMemo, useState } from 'react';

import { blurActiveElement } from '@/utils';
import { PLATFORMS, PLATFORM_CAPTIONS } from '@/constants';

const { Link } = rrd.default ?? rrd;

const PLATFORMS_LIST = Object.values(PLATFORMS);

// Convert internal platform keys to URL slugs used by your SEO routes
const toSlug = (platform) => {
  if (platform === 'apple') return 'apple-music';
  if (platform === 'youtube') return 'youtube-music';
  return platform;
};

const toCaption = (platform) => PLATFORM_CAPTIONS[platform] || platform;

const buildPairsBySource = () => {
  return PLATFORMS_LIST.map((source) => {
    const targets = PLATFORMS_LIST.filter((t) => t !== source);
    return { source, targets };
  });
};

const getLogo = (platform) => {
  if (platform === 'spotify') return '/brand/spotify/logo.svg';
  if (platform === 'youtube') return '/brand/youtube/logo.svg';
  if (platform === 'apple') return '/brand/apple/logo.svg';
  return null;
};

const buildRoute = (source, target) => {
  const sourceSlug = toSlug(source);
  const targetSlug = toSlug(target);
  return `/convert/${sourceSlug}-to-${targetSlug}`;
};

// Popular cross-platform conversions
const POPULAR_PAIRS = [
  ['spotify', 'apple'],
  ['spotify', 'youtube'],
  ['apple', 'spotify'],
  ['apple', 'youtube'],
  ['youtube', 'apple'],
  ['youtube', 'spotify'],
];

const Converters = () => {
  const groups = useMemo(() => buildPairsBySource(), []);

  const [openSource, setOpenSource] = useState('');
  const [activeSource, setActiveSource] = useState('');

  // Closed by default
  const [allOptionsOpen, setAllOptionsOpen] = useState(false);

  const filteredGroups = useMemo(() => {
    return groups
      .filter(({ source }) => !activeSource || source === activeSource)
      .filter(({ targets }) => targets.length > 0);
  }, [groups, activeSource]);

  const totalOptions = useMemo(() => {
    return filteredGroups.reduce((acc, g) => acc + g.targets.length, 0);
  }, [filteredGroups]);

  const popularCards = useMemo(() => {
    const valid = new Set(PLATFORMS_LIST);
    return POPULAR_PAIRS.filter(
      ([s, t]) => valid.has(s) && valid.has(t) && s !== t
    );
  }, []);

  const hasActiveFilter = Boolean(activeSource);

  return (
    <>
      {/* Default */}
      <div className="mb-4">
        <Link to="/convert" className="text-decoration-none">
          <div className="border border-2 rounded p-3 h-100 bg-body">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div className="pe-2" style={{ minWidth: 0 }}>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="fw-semibold text-accent">Custom</div>
                  <span className="badge text-bg-light">Recommended</span>
                </div>

                <div className="small text-muted">
                  Choose any source and target, including same platform
                  transfers.
                </div>
              </div>

              <i
                className="fas fa-chevron-right text-muted"
                aria-hidden="true"
              />
            </div>
          </div>
        </Link>
      </div>

      {/* Popular */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h5 className="mb-0">Popular</h5>
          {/*<a*/}
          {/*  href="#all-options"*/}
          {/*  className="small text-decoration-none text-secondary"*/}
          {/*>*/}
          {/*  View all*/}
          {/*</a>*/}
        </div>

        <div className="row g-3">
          {popularCards.map(([source, target]) => {
            const sourceCaption = toCaption(source);
            const targetCaption = toCaption(target);
            const to = buildRoute(source, target);

            return (
              <div
                className="col-12 col-md-6 col-lg-4"
                key={`${source}-${target}`}
              >
                <Link
                  to={to}
                  className="text-decoration-none"
                  aria-label={`Transfer ${sourceCaption} to ${targetCaption}`}
                >
                  <div className="border rounded p-3 h-100 bg-body">
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <div className="fw-semibold text-accent">
                        {sourceCaption} → {targetCaption}
                      </div>

                      <i
                        className="fas fa-chevron-right text-muted"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      {/* All Options */}
      <div id="all-options" className="mb-3">
        {/* Collapsible header (styled like a card) */}
        <button
          type="button"
          className="w-100 text-start border rounded p-3 bg-body"
          onClick={() => {
            blurActiveElement();
            setAllOptionsOpen((v) => !v);
          }}
          aria-expanded={allOptionsOpen}
          aria-controls="all-options-body"
          // subtle hover without needing custom CSS files
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex align-items-start justify-content-between gap-3">
            <div style={{ minWidth: 0 }}>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h5 className="mb-0">All options</h5>

                <span className="badge text-bg-light">
                  {filteredGroups.length} sources
                </span>

                <span className="badge text-bg-light">
                  {totalOptions} options
                </span>
              </div>

              <div className="small text-muted mt-1">
                Browse every source → target pair. Filter by source to narrow it
                down.
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <span className="small text-muted">
                {allOptionsOpen ? 'Hide' : 'Show'}
              </span>
              <i
                className={`fas ${
                  allOptionsOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                } text-muted`}
                aria-hidden="true"
              />
            </div>
          </div>
        </button>

        {/* Body */}
        {allOptionsOpen && (
          <div id="all-options-body" className="mt-3">
            {/* Filter toolbar */}
            <div className="border rounded p-3 mb-3 bg-body">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="small text-muted flex-shrink-0">Source</div>

                <div
                  className="d-flex align-items-center flex-wrap"
                  style={{ minWidth: 0 }}
                >
                  <div className="d-flex flex-wrap gap-2">
                    {PLATFORMS_LIST.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`btn btn-sm ${
                          activeSource === p
                            ? 'btn-accent'
                            : 'btn-outline-secondary'
                        }`}
                        onClick={() => {
                          blurActiveElement();
                          setActiveSource(p);
                          setOpenSource(p);
                        }}
                      >
                        {toCaption(p)}
                      </button>
                    ))}
                  </div>

                  {hasActiveFilter && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-decoration-none ms-1"
                      onClick={() => {
                        blurActiveElement();
                        setActiveSource('');
                        setOpenSource('');
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Source groups */}
            <div className="border rounded bg-body overflow-hidden">
              {filteredGroups.map(({ source, targets }, idx) => {
                const sourceCaption = toCaption(source);
                const logo = getLogo(source);
                const isOpen = openSource === source;

                return (
                  <div key={source} className={idx ? 'border-top' : ''}>
                    <button
                      type="button"
                      className="w-100 text-start p-3 bg-transparent border-0"
                      onClick={() => setOpenSource(isOpen ? '' : source)}
                      aria-expanded={isOpen}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-2">
                          {logo && (
                            <img
                              src={logo}
                              alt={sourceCaption}
                              style={{ width: '1.6rem', height: '1.6rem' }}
                              loading="lazy"
                              decoding="async"
                            />
                          )}
                          <div className="fw-semibold">{sourceCaption}</div>
                          <span className="badge text-bg-light">
                            {targets.length}
                          </span>
                        </div>

                        <i
                          className={`fas ${
                            isOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                          } text-muted`}
                          aria-hidden="true"
                        />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-3 pb-3">
                        <div className="row g-2">
                          {targets.map((target) => {
                            const targetCaption = toCaption(target);
                            const to = buildRoute(source, target);

                            return (
                              <div
                                className="col-12 col-sm-6 col-lg-4"
                                key={`${source}-${target}`}
                              >
                                <Link to={to} className="text-decoration-none">
                                  <div className="border rounded p-2 h-100">
                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                      <div className="small fw-semibold">
                                        {sourceCaption} → {targetCaption}
                                      </div>
                                      <i
                                        className="fas fa-chevron-right text-muted"
                                        aria-hidden="true"
                                      />
                                    </div>
                                  </div>
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Converters;
